import { useCallback, useEffect, useState } from "react"
import {
  isLegacyAddress,
  isNativeSegwitAddress,
  RevealDetails,
} from "@mezo/sdk"
import { PayloadAction } from "@reduxjs/toolkit"
import { useInterval } from "usehooks-ts"
import { useAccount } from "wagmi"

import {
  checkBitcoinDepositedAmount,
  depositBitcoinFromAnotherWallet,
  generateBitcoinDepositDetails,
  selectDepositAmount,
  selectDepositStatus,
  selectLockTime,
  setDepositAmount,
  setDepositStatus,
  setIsProcessingTransaction,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import {
  BITCOIN_MIN_DEPOSIT,
  BTC_DEPOSIT_CONFIRMATION_INTERVAL,
  ENABLE_ZERO_LOCK_TIME,
  SDK_NETWORK,
  TBTC_DEPOSIT_BITCOIN_RECOVERY_ADDRESS,
} from "#/shared/constants"
import {
  convertBitcoinToSatoshi,
  convertSatoshiToBitcoin,
  convertWeeksToSeconds,
} from "#/shared/utils"

import { isPassportConnector } from "./wagmi"

export const BITCOIN_MIN_DEPOSIT_SATOSHI =
  convertBitcoinToSatoshi(BITCOIN_MIN_DEPOSIT)

export const useBitcoinRecoveryAddress = () => {
  const { connector } = useAccount()
  const [address, setAddress] = useState<string | null>(null)

  useEffect(() => {
    const fetchBitcoinAddress = async () => {
      if (!connector) {
        setAddress(null)
        return
      }

      if (isPassportConnector(connector)) {
        // TODO: Set correct type. We need to export the PassportWagmiConnector type.
        // @ts-expect-error adjust types to handle bitcoin wallet wrappers
        let bitcoinRecoveryAddress = await connector?.getBitcoinAddress()

        if (
          !isLegacyAddress(bitcoinRecoveryAddress, SDK_NETWORK) &&
          !isNativeSegwitAddress(bitcoinRecoveryAddress, SDK_NETWORK)
        ) {
          // Only P2PKH or P2WPKH address can be used as recovery address in tBTC
          // v2.
          bitcoinRecoveryAddress = TBTC_DEPOSIT_BITCOIN_RECOVERY_ADDRESS
        }

        setAddress(bitcoinRecoveryAddress)
        return
      }

      setAddress(TBTC_DEPOSIT_BITCOIN_RECOVERY_ADDRESS)
    }

    fetchBitcoinAddress()
  }, [connector])

  return address
}

export const useBitcoinBridgeDepositDetails = () => {
  const lockTime = useDappSelector(selectLockTime)
  const parsedLockPeriod = convertWeeksToSeconds(lockTime.period)

  const dispatch = useDappDispatch()

  const [depositDetails, setDepositDetails] = useState<RevealDetails | null>(
    null,
  )

  const bitcoinRecoveryAddress = useBitcoinRecoveryAddress()

  useEffect(() => {
    const fetchBitcoinBridgeDepositDetails = async () => {
      if (
        !bitcoinRecoveryAddress ||
        // To enable deposit with no lock time for dev purposes
        (!ENABLE_ZERO_LOCK_TIME && !parsedLockPeriod)
      ) {
        return
      }

      const { payload: details } = await dispatch(
        generateBitcoinDepositDetails({
          lockPeriod: parsedLockPeriod,
          bitcoinRecoveryAddress,
        }),
      )

      if (!details) {
        setDepositDetails(null)
        return
      }

      setDepositDetails(details as RevealDetails)
    }

    fetchBitcoinBridgeDepositDetails()
  }, [dispatch, parsedLockPeriod, bitcoinRecoveryAddress])

  return depositDetails
}

export const useDetectBitcoinFunds = (depositAddress: string | null) => {
  const dispatch = useDappDispatch()

  const [detectedDepositAmount, setDetectedDepositAmount] = useState<bigint>(0n)
  const [hasDeposited, setHasDeposited] = useState(false)
  const [sentInsufficientFunds, setSentInsufficientFunds] = useState(false)

  const handleStatusCheck = useCallback(async () => {
    if (!depositAddress) return

    const { payload: amountDepositedInSatoshi } = (await dispatch(
      checkBitcoinDepositedAmount({ address: depositAddress }),
    )) as PayloadAction<bigint>

    setDetectedDepositAmount(amountDepositedInSatoshi)

    if (amountDepositedInSatoshi === 0n) return

    dispatch(
      setDepositAmount(
        convertSatoshiToBitcoin(amountDepositedInSatoshi).toString(),
      ),
    )

    if (amountDepositedInSatoshi < BigInt(BITCOIN_MIN_DEPOSIT_SATOSHI)) {
      setSentInsufficientFunds(true)
    } else {
      setHasDeposited(true)
    }
  }, [depositAddress, dispatch])

  useInterval(
    handleStatusCheck,
    !hasDeposited && depositAddress ? BTC_DEPOSIT_CONFIRMATION_INTERVAL : null,
  )

  return {
    hasDeposited,
    sentInsufficientFunds,
    detectedDepositAmount,
  }
}

export const useBitcoinDepositFromAnotherWallet = () => {
  const dispatch = useDappDispatch()

  const depositStatus = useDappSelector(selectDepositStatus)

  const depositAmount = useDappSelector(selectDepositAmount)
  const amountInSatoshis = convertBitcoinToSatoshi(depositAmount)

  const lockTime = useDappSelector(selectLockTime)
  const parsedLockPeriod = convertWeeksToSeconds(lockTime.period)

  const [hasError, setHasError] = useState(false)

  const [hasDepositedFundsToPortal, setHasDepositedFundsToPortal] =
    useState(false)

  const bitcoinBridgeDepositDetails = useBitcoinBridgeDepositDetails()

  const bitcoinRecoveryAddress = useBitcoinRecoveryAddress()

  const {
    hasDeposited: hasDepositedEnoughFundsToBridge,
    sentInsufficientFunds,
    detectedDepositAmount,
  } = useDetectBitcoinFunds(
    bitcoinBridgeDepositDetails?.btcDepositAddress ?? null,
  )

  const hasDepositedSuccessfully =
    hasDepositedEnoughFundsToBridge && hasDepositedFundsToPortal

  const canInitiateDeposit =
    depositStatus === "idle" && // must be idle to initiate deposit
    !hasDepositedFundsToPortal && // guards against depositing the same thing twice
    amountInSatoshis === detectedDepositAmount && // amount deposited must be the same as expected amount
    bitcoinBridgeDepositDetails?.revealData && // release data must be available
    bitcoinRecoveryAddress && // recovery address must be available
    hasDepositedEnoughFundsToBridge // must have deposited more than the minimum required

  // Update deposit status to completed when deposit is successful
  useEffect(() => {
    if (!hasDepositedSuccessfully) return
    dispatch(setDepositStatus("completed"))
  }, [hasDepositedSuccessfully, dispatch])

  // Protect users from closing the website while the transaction is being processed
  useEffect(() => {
    dispatch(setIsProcessingTransaction(true))

    return () => {
      dispatch(setIsProcessingTransaction(false))
    }
  }, [dispatch])

  // Bridge and deposit to Portal
  useEffect(() => {
    const deposit = async () => {
      if (canInitiateDeposit) {
        // Avoid creating multiple deposits because of useEffect re-run
        dispatch(setDepositStatus("pending"))

        const { payload: deposited } = await dispatch(
          depositBitcoinFromAnotherWallet({
            lockPeriod: parsedLockPeriod,
            amount: Number(amountInSatoshis),
            revealData: bitcoinBridgeDepositDetails.revealData,
          }),
        )

        setHasDepositedFundsToPortal(true)
        setHasError(!deposited) // TODO: we are not handling unexpected errors yet
      }
    }

    deposit()
  }, [
    parsedLockPeriod,
    amountInSatoshis,
    bitcoinBridgeDepositDetails?.revealData,
    canInitiateDeposit,
    dispatch,
  ])

  return {
    bitcoinBridgeDepositAddres:
      bitcoinBridgeDepositDetails?.btcDepositAddress ?? null,
    hasDepositedEnoughFundsToBridge,
    sentInsufficientFunds,
    hasDepositedFundsToPortal,
    hasDepositedSuccessfully,
    hasError,
  }
}
